import { translationService } from './translationService';

/**
 * Translates a quiz prompt (enunciado) according to strict rules:
 * 1. Translate ONLY the prompt text before colon (:).
 * 2. Anything after colon (:) MUST NOT be translated.
 * 3. Content inside single quotes ('...') or double quotes ("...") MUST NOT be translated.
 * 4. Alternatives/options are NEVER translated (handled at UI level).
 */
export async function translateQuizPrompt(
  promptText: string,
  targetLang: string,
  sourceLang?: string
): Promise<string> {
  if (!promptText || !promptText.trim()) return promptText;

  // 1. Separate before and after colon (:)
  const colonIndex = promptText.indexOf(':');
  let textToTranslate = promptText;
  let textAfterColon = '';

  if (colonIndex !== -1) {
    textToTranslate = promptText.substring(0, colonIndex);
    textAfterColon = promptText.substring(colonIndex);
  }

  // 2. Extract and preserve quoted strings ("...", '...', “...”, ‘...’)
  const placeholders: string[] = [];

  // Match double quotes, single quotes, and typographic smart quotes
  const quoteRegex = /("[^"]+"|\B'[^']+'\B|'[^']+'|“[^”]+”|‘[^’]+’)/g;

  const cleanToTranslate = textToTranslate.replace(quoteRegex, (match) => {
    const idx = placeholders.length;
    placeholders.push(match);
    return `___KEEP_QUOTE_${idx}___`;
  });

  try {
    const res = await translationService.unifiedTranslate(
      cleanToTranslate,
      targetLang,
      undefined,
      sourceLang,
      undefined,
      false,
      false,
      { isGrammar: true }
    );

    let translated = res.translation || cleanToTranslate;

    // 3. Restore preserved quoted strings
    placeholders.forEach((originalQuote, idx) => {
      translated = translated.replace(new RegExp(`___KEEP_QUOTE_${idx}___`, 'g'), originalQuote);
    });

    return translated + textAfterColon;
  } catch (err) {
    console.warn('[quizTranslationService] Failed to translate prompt:', err);
    return promptText;
  }
}

/**
 * Translates a quiz error explanation according to strict rules:
 * 1. Content inside double quotes ("..."), single quotes ('...'), or bold (**...**, <b>...</b>, <strong>...</strong>)
 *    MUST NOT be translated.
 */
export async function translateQuizExplanation(
  explanationText: string,
  targetLang: string,
  sourceLang?: string
): Promise<string> {
  if (!explanationText || !explanationText.trim()) return explanationText;

  const placeholders: string[] = [];

  // Match bold tags/markdown, double quotes, single quotes
  const protectRegex = /(\*\*.*?\*\*|<b>.*?<\/b>|<strong>.*?<\/strong>|"[^"]+"|'[^']{2,}')/g;

  const cleanText = explanationText.replace(protectRegex, (match) => {
    const idx = placeholders.length;
    placeholders.push(match);
    return `___KEEP_EXP_${idx}___`;
  });

  try {
    const res = await translationService.unifiedTranslate(
      cleanText,
      targetLang,
      undefined,
      sourceLang,
      undefined,
      false,
      false,
      { isGrammar: true }
    );

    let translated = res.translation || cleanText;

    // Restore preserved segments
    placeholders.forEach((original, idx) => {
      translated = translated.replace(new RegExp(`___KEEP_EXP_${idx}___`, 'g'), original);
    });

    return translated;
  } catch (err) {
    console.warn('[quizTranslationService] Failed to translate explanation:', err);
    return explanationText;
  }
}
